import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';

/** Receives both keydown and keyup; check event.type. */
export type FeedbackKeyHandler = (event: KeyboardEvent) => void;

/** Where the picker and the widget get their keys from. */
export interface FeedbackKeySource {
  /** Adds a keydown/keyup handler; returns its removal. */
  listen(handler: FeedbackKeyHandler): () => void;
}

/** Own nodes of the feature: the widget (host, panels, highlight, overlay pane) and its launcher. */
const OWN = '[data-feedback-ignore]';

/**
 * The one keyboard entry point of the open widget. Eager and tiny on purpose: it imports nothing of the
 * widget, so the widget chunk stays lazy.
 *
 * FeedbackWidgetService creates it at app start (the service is injected by AppComponent), so its window
 * capture listeners come before every page key listener: document capture/bubble ones (GridSearch Enter →
 * findNext, document:keydown HostListeners) and window capture ones that pages add later (LoadingRunner
 * Space/ArrowUp). Handlers (widget, picker) run in registration order; one that calls
 * stopImmediatePropagation ends the round, as separate listeners on one target would.
 *
 * While the widget is open (claimed), a key whose target is one of the feature's own nodes stops here after
 * the handlers, so no page listener sees it. Only propagation stops, the default action stays: a focused
 * button is pressed by Enter/Space, the textarea types and breaks lines, Tab moves focus. Nothing inside the
 * widget listens for keys on its nodes (the template binds only click/input; ats-button is a native button
 * with (click); cdkTrapFocus works with focus anchors; atsTooltip reacts to keydown.escape only with
 * atsTooltipFocus, which the widget does not use), so stopping at window costs the widget no handler.
 * Keys in a dialog over the widget or on the page are not the widget's: they go on as usual.
 *
 * Closed widget: no handlers, no claims — the listeners return at once, outside the Angular zone, and pages
 * get their keys exactly as before.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackKeyboard implements FeedbackKeySource {
  private readonly zone = inject(NgZone);
  private readonly handlers = new Set<FeedbackKeyHandler>();
  private claims = 0;

  constructor() {
    const win = inject(DOCUMENT).defaultView ?? window;
    this.zone.runOutsideAngular(() => {
      win.addEventListener('keydown', this.dispatch, true);
      win.addEventListener('keyup', this.dispatch, true);
    });
    inject(DestroyRef).onDestroy(() => {
      win.removeEventListener('keydown', this.dispatch, true);
      win.removeEventListener('keyup', this.dispatch, true);
      this.handlers.clear();
      this.claims = 0;
    });
  }

  listen(handler: FeedbackKeyHandler): () => void {
    // A fresh entry per call: the same function may be added twice and removed independently.
    const entry: FeedbackKeyHandler = event => handler(event);
    this.handlers.add(entry);
    return () => this.handlers.delete(entry);
  }

  /** Marks the widget open: keys on the feature's own nodes stay with it. Returns the (idempotent) release. */
  claim(): () => void {
    let held = true;
    this.claims++;
    return () => {
      if (!held) return;
      held = false;
      this.claims--;
    };
  }

  private dispatch = (event: KeyboardEvent): void => {
    if (!this.handlers.size && !this.claims) return;
    this.zone.run(() => {
      for (const handler of [...this.handlers]) {
        // A handler may remove another one (Escape closes the widget and stops the picker).
        if (!this.handlers.has(handler)) continue;
        handler(event);
        if (event.cancelBubble) break;
      }
    });
    if (this.claims && event.target instanceof Element && event.target.closest(OWN)) event.stopImmediatePropagation();
  };
}
